import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  BuildingOfficeIcon, 
  DocumentTextIcon,
  CubeIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  UserIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import api from '../../../services/api';
import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import LogoUpload from '../../../components/ui/LogoUpload';
import CoverImageUpload from '../../../components/ui/CoverImageUpload';
import ImageUpload from '../../../components/ui/ImageUpload';
import PlaceBusinessHoursMatrix from '../../../components/PlaceBusinessHoursMatrix';
import MultiSelectChips from '../../../components/ui/MultiSelectChips';
import { Category, Characteristic } from '../../../types/categories';
import { placeService } from '../../../services/api';

interface PlaceData {
  id: string;
  name: string;
  description: string;
  address: string;
  city: string | null;
  state: string | null;
  status: string;
  logoUrl: string;
  coverImageUrl: string;
  instagram: string;
  redeemCode: string | null;
  isRedeemCodeEnabled: boolean;
  businessUserInfo?: {
    id: string;
    name: string | null;
    role: string;
  };
  // Dados empresariais
  companyName?: string;
  cnpj?: string;
  phone?: string;
  email?: string;
  website?: string;
  businessHours?: string;
  // Produtos
  products?: Product[];
}

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  category: string;
  isAvailable: boolean;
}

interface UpdateBasicInfoRequest {
  name: string;
  description: string;
  address: string;
  instagram: string;
}

interface UpdateBusinessInfoRequest {
  companyName?: string;
  taxId?: string;  // CNPJ
  website?: string;
  businessEmail?: string;  // Email
  businessPhone?: string;  // Phone
}

const PlaceEditPage: React.FC = () => {
  const { placeId } = useParams<{ placeId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  
  const [activeSection, setActiveSection] = useState<'basic' | 'business' | 'hours' | 'products' | 'users'>('basic');
  const [basicInfo, setBasicInfo] = useState<UpdateBasicInfoRequest>({
    name: '',
    description: '',
    address: '',
    instagram: ''
  });
  const [businessInfo, setBusinessInfo] = useState<UpdateBusinessInfoRequest>({
    companyName: '',
    taxId: '',
    website: '',
    businessEmail: '',
    businessPhone: ''
  });

  // Categories and Characteristics state
  const [availableCategories, setAvailableCategories] = useState<Category[]>([]);
  const [availableCharacteristics, setAvailableCharacteristics] = useState<Characteristic[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedCharacteristicIds, setSelectedCharacteristicIds] = useState<string[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);
  const [characteristicsLoading, setCharacteristicsLoading] = useState(false);

  // Buscar dados da cafeteria
  const { data: place, isLoading: isLoadingPlace } = useQuery({
    queryKey: ['place', placeId],
    queryFn: async () => {
      const response = await api.get(`/admin/places/${placeId}`);
      return response.data as PlaceData;
    },
    enabled: !!placeId
  });



  // Atualizar dados básicos
  const updateBasicInfoMutation = useMutation({
    mutationFn: async (data: UpdateBasicInfoRequest) => {
      await api.put(`/admin/places/${placeId}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
      queryClient.invalidateQueries({ queryKey: ['places'] });
    }
  });

  // Atualizar dados empresariais
  const updateBusinessInfoMutation = useMutation({
    mutationFn: async (data: UpdateBusinessInfoRequest) => {
      await api.put(`/admin/places/${placeId}/business-info`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
    }
  });

  // Atualizar logo
  const updateLogoMutation = useMutation({
    mutationFn: async (logoUrl: string) => {
      await api.put(`/admin/places/${placeId}/logo`, { logoUrl });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
    }
  });

  // Atualizar imagem de capa
  const updateCoverImageMutation = useMutation({
    mutationFn: async (coverImageUrl: string) => {
      await api.put(`/admin/places/${placeId}/cover-image`, { coverImageUrl });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['place', placeId] });
    }
  });

  // Inicializar formulários quando os dados carregarem
  useEffect(() => {
    if (place) {
      setBasicInfo({
        name: place.name,
        description: place.description,
        address: place.address,
        instagram: place.instagram
      });
      
      if (place.cnpj) {
        setBusinessInfo({
          companyName: place.companyName || '',
          taxId: place.cnpj,
          website: place.website || '',
          businessEmail: place.email || '',
          businessPhone: place.phone || ''
        });
      }
      
      // Load categories and characteristics when place data is available
      loadCategoriesAndCharacteristics();
    }
  }, [place]);

  const loadCategoriesAndCharacteristics = async () => {
    if (!placeId) return;
    
    try {
      setCategoriesLoading(true);
      setCharacteristicsLoading(true);
      
      const [categories, characteristics] = await Promise.all([
        placeService.getCategories(),
        placeService.getCharacteristics()
      ]);
      
      setAvailableCategories(categories);
      setAvailableCharacteristics(characteristics);
      
      const [placeCategories, placeCharacteristics] = await Promise.all([
        placeService.getPlaceCategories(placeId),
        placeService.getPlaceCharacteristics(placeId)
      ]);
      
      setSelectedCategoryIds(placeCategories.map(cat => cat.id));
      setSelectedCharacteristicIds(placeCharacteristics.map(char => char.id));
    } catch (err: any) {
      console.error('Erro ao carregar categorias e características:', err);
    } finally {
      setCategoriesLoading(false);
      setCharacteristicsLoading(false);
    }
  };

  const handleBasicInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      // Update basic info first
      await updateBasicInfoMutation.mutateAsync(basicInfo);
      
      // Then update categories and characteristics
      await Promise.all([
        placeService.updatePlaceCategories(placeId!, selectedCategoryIds),
        placeService.updatePlaceCharacteristics(placeId!, selectedCharacteristicIds)
      ]);
    } catch (error) {
      console.error('Erro ao salvar dados básicos:', error);
    }
  };

  const handleBusinessInfoSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    updateBusinessInfoMutation.mutate(businessInfo);
  };

  const handleLogoUpload = (imageUrl: string) => {
    updateLogoMutation.mutate(imageUrl);
  };

  const handleCoverImageUpload = (imageUrl: string) => {
    updateCoverImageMutation.mutate(imageUrl);
  };

  if (isLoadingPlace) {
    return <LoadingSpinner />;
  }

  if (!place) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900">Cafeteria não encontrada</h2>
          <button
            onClick={() => navigate('/places')}
            className="btn btn-primary mt-4"
          >
            Voltar para Lista
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-4">
          <button
            onClick={() => navigate('/places')}
            className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div className="flex items-center">
            {place?.logoUrl ? (
              <img 
                src={place.logoUrl} 
                alt="Logo da cafeteria" 
                className="h-12 w-12 rounded-lg object-cover mr-3"
              />
            ) : (
              <BuildingOfficeIcon className="h-8 w-8 text-gray-400 mr-3" />
            )}
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Editar Cafeteria</h1>
              <p className="text-gray-600">{place.name}</p>
            </div>
          </div>
        </div>

        {/* Navegação das seções */}
        <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg">
          <button
            onClick={() => setActiveSection('basic')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'basic'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <BuildingOfficeIcon className="h-4 w-4" />
              Dados Básicos
            </div>
          </button>
          <button
            onClick={() => setActiveSection('business')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'business'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <DocumentTextIcon className="h-4 w-4" />
              Dados Empresariais
            </div>
          </button>
          <button
            onClick={() => setActiveSection('hours')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'hours'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <ClockIcon className="h-4 w-4" />
              Horários
            </div>
          </button>
          <button
            onClick={() => setActiveSection('products')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'products'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <CubeIcon className="h-4 w-2" />
              Produtos
            </div>
          </button>
          <button
            onClick={() => setActiveSection('users')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeSection === 'users'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <div className="flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              Usuários
            </div>
          </button>
        </div>
      </div>

      {/* Seção Ativa */}
      <div className="bg-white rounded-lg shadow">
        {activeSection === 'basic' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Dados Básicos</h2>
            
            <form onSubmit={handleBasicInfoSubmit} className="space-y-6">
              {/* Informações básicas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nome da Cafeteria
                  </label>
                  <input
                    type="text"
                    value={basicInfo.name}
                    onChange={(e) => setBasicInfo({ ...basicInfo, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Instagram
                  </label>
                  <input
                    type="text"
                    value={basicInfo.instagram}
                    onChange={(e) => setBasicInfo({ ...basicInfo, instagram: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="@cafeteria"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descrição
                </label>
                <textarea
                  value={basicInfo.description}
                  onChange={(e) => setBasicInfo({ ...basicInfo, description: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Descreva sua cafeteria..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Endereço
                </label>
                <input
                  type="text"
                  value={basicInfo.address}
                  onChange={(e) => setBasicInfo({ ...basicInfo, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  required
                />
              </div>

              {/* Imagens */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Logo
                  </label>
                  <LogoUpload
                    currentImageUrl={place.logoUrl}
                    onImageUploaded={handleLogoUpload}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Imagem de Capa
                  </label>
                  <CoverImageUpload
                    currentImageUrl={place.coverImageUrl}
                    onImageUploaded={handleCoverImageUpload}
                  />
                </div>
              </div>

              {/* Categorias e Características */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Categorias */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Categorias
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Selecione as categorias que melhor descrevem o estabelecimento
                  </p>
                  {categoriesLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Carregando categorias...</span>
                    </div>
                  ) : (
                    <MultiSelectChips
                      items={availableCategories}
                      selectedIds={selectedCategoryIds}
                      onSelectionChange={setSelectedCategoryIds}
                      placeholder="Selecione as categorias..."
                      disabled={updateBasicInfoMutation.isPending}
                    />
                  )}
                </div>

                {/* Características */}
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Características
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Selecione as características que o estabelecimento oferece
                  </p>
                  {characteristicsLoading ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-emerald-600"></div>
                      <span className="ml-2 text-sm text-gray-500">Carregando características...</span>
                    </div>
                  ) : (
                    <MultiSelectChips
                      items={availableCharacteristics}
                      selectedIds={selectedCharacteristicIds}
                      onSelectionChange={setSelectedCharacteristicIds}
                      placeholder="Selecione as características..."
                      disabled={updateBasicInfoMutation.isPending}
                    />
                  )}
                </div>
              </div>

              {/* Botão salvar */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updateBasicInfoMutation.isPending}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {updateBasicInfoMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      Salvar Dados Básicos
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeSection === 'business' && (
          <div className="p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-6">Dados Empresariais</h2>
            
            <form onSubmit={handleBusinessInfoSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Razão Social
                  </label>
                  <input
                    type="text"
                    value={businessInfo.companyName || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, companyName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="Nome da empresa"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CNPJ
                  </label>
                  <input
                    type="text"
                    value={businessInfo.taxId || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, taxId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="00.000.000/0000-00"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    value={businessInfo.businessPhone || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, businessPhone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="(11) 99999-9999"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    value={businessInfo.businessEmail || ''}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, businessEmail: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="contato@cafeteria.com"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Website
                  </label>
                  <input
                    type="url"
                    value={businessInfo.website}
                    onChange={(e) => setBusinessInfo({ ...businessInfo, website: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                    placeholder="https://www.cafeteria.com"
                  />
                </div>
              </div>


              {/* Botão salvar */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={updateBusinessInfoMutation.isPending}
                  className="px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {updateBusinessInfoMutation.isPending ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Salvando...
                    </>
                  ) : (
                    <>
                      <CheckCircleIcon className="h-4 w-4" />
                      Salvar Dados Empresariais
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {activeSection === 'hours' && (
          <div className="p-6">
            <PlaceBusinessHoursMatrix placeId={placeId!} />
          </div>
        )}

        {activeSection === 'products' && (
          <PlaceProductsSection placeId={placeId!} />
        )}

        {activeSection === 'users' && (
          <PlaceUsersSection placeId={placeId!} />
        )}
      </div>
    </div>
  );
};

// Componente para gerenciar produtos do lugar - Clone completo da gestão da cafeteria
const PlaceProductsSection: React.FC<{ placeId: string }> = ({ placeId }) => {
  const queryClient = useQueryClient();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  
  // Função para traduzir frequência de uso
  const getUsageFrequencyLabel = (frequency: string): string => {
    switch (frequency) {
      case 'unlimited':
        return 'Ilimitado';
      case 'daily':
        return 'Diário';
      case 'weekly':
        return 'Semanal';
      case 'biweekly':
        return 'Quinzenal';
      case 'monthly':
        return 'Mensal';
      default:
        return frequency;
    }
  };
  
  const [productForm, setProductForm] = useState({
    name: '',
    description: '',
    originalPrice: '',
    discountedPrice: '',
    discountPercentage: '',
    category: 'Coffee',
    usageFrequency: 'unlimited',
    isAvailable: true,
    isRequiredProduct: false,
    isBlocked: false,
    hasTimeRestriction: false,
    availableFrom: '',
    availableTo: '',
    imageUrl: ''
  });

  // Buscar produtos do lugar
  const { data: products, isLoading: isLoadingProducts, refetch: refetchProducts } = useQuery({
    queryKey: ['place-products', placeId],
    queryFn: async () => {
      const response = await api.get(`/admin/places/${placeId}/products`);
      return response.data;
    },
    enabled: !!placeId
  });

  // Mutação para criar produto
  const createProductMutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await api.post(`/admin/places/${placeId}/products`, data);
      return response.data;
    },
    onSuccess: () => {
      // Invalidar cache explicitamente
      queryClient.invalidateQueries({ queryKey: ['place-products', placeId] });
      refetchProducts();
      setShowCreateForm(false);
      resetProductForm();
    },
    onError: (error: any) => {
      console.error('Erro ao criar produto:', error);
      alert('Erro ao criar produto');
    }
  });

  // Mutação para atualizar produto
  const updateProductMutation = useMutation({
    mutationFn: async (data: any) => {
      if (!editingProduct?.id) {
        throw new Error('ID do produto não encontrado');
      }
      const productId = editingProduct.id.toString();
      const url = `/admin/places/${placeId}/products/${productId}`;
      console.log('Updating product:', { placeId, productId, url });
      const response = await api.put(url, data);
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidar cache explicitamente
      queryClient.invalidateQueries({ queryKey: ['place-products', placeId] });
      refetchProducts();
      setEditingProduct(null);
      resetProductForm();
      if (data) {
        // Se o backend retornou o produto atualizado, podemos usá-lo para atualizar a UI
        console.log('Product updated successfully:', data);
      }
    },
    onError: (error: any) => {
      console.error('Erro ao atualizar produto:', error);
      const errorMessage = error.response?.data?.error || error.message || 'Erro ao atualizar produto';
      alert(errorMessage);
    }
  });

  // Mutação para deletar produto
  const deleteProductMutation = useMutation({
    mutationFn: async (productId: string) => {
      const response = await api.delete(`/admin/places/${placeId}/products/${productId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidar cache explicitamente
      queryClient.invalidateQueries({ queryKey: ['place-products', placeId] });
      refetchProducts();
    },
    onError: (error: any) => {
      console.error('Erro ao deletar produto:', error);
      alert('Erro ao deletar produto');
    }
  });

  const resetProductForm = () => {
    setProductForm({
      name: '',
      description: '',
      originalPrice: '',
      discountedPrice: '',
      discountPercentage: '',
      category: 'Coffee',
      usageFrequency: 'unlimited',
      isAvailable: true,
      isRequiredProduct: false,
      isBlocked: false,
      hasTimeRestriction: false,
      availableFrom: '',
      availableTo: '',
      imageUrl: ''
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const formData = {
      ...productForm,
      originalPrice: parseFloat(productForm.originalPrice) || 0,
      discountedPrice: productForm.discountedPrice ? parseFloat(productForm.discountedPrice) : null,
      discountPercentage: productForm.discountPercentage ? parseFloat(productForm.discountPercentage) : null,
      availableFrom: productForm.hasTimeRestriction && productForm.availableFrom ? new Date(productForm.availableFrom).toISOString() : null,
      availableTo: productForm.hasTimeRestriction && productForm.availableTo ? new Date(productForm.availableTo).toISOString() : null
    };

    if (editingProduct && editingProduct.id) {
      updateProductMutation.mutate(formData);
    } else {
      createProductMutation.mutate(formData);
    }
  };

  // Função para normalizar a frequência de uso vinda da API
  const normalizeUsageFrequency = (frequency: any): string => {
    if (frequency === null || frequency === undefined || frequency === '') {
      return 'unlimited';
    }
    
    // Se for número, mapear para string (caso o enum venha como índice)
    if (typeof frequency === 'number') {
      const frequencyMap: Record<number, string> = {
        0: 'unlimited',
        1: 'daily',
        2: 'weekly',
        3: 'biweekly',
        4: 'monthly'
      };
      return frequencyMap[frequency] || 'unlimited';
    }
    
    // Converter para string e normalizar para minúsculas
    const freqStr = String(frequency).toLowerCase();
    
    // Valores válidos esperados
    const validFrequencies = ['unlimited', 'daily', 'weekly', 'biweekly', 'monthly'];
    
    // Se o valor já está no formato correto, retornar
    if (validFrequencies.includes(freqStr)) {
      return freqStr;
    }
    
    // Caso contrário, retornar 'unlimited' como padrão
    return 'unlimited';
  };

  const handleEdit = (product: any) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name || '',
      description: product.description || '',
      originalPrice: product.originalPrice?.toString() || '',
      discountedPrice: product.discountedPrice?.toString() || '',
      discountPercentage: product.discountPercentage?.toString() || '',
      category: product.category || 'Coffee',
      usageFrequency: normalizeUsageFrequency(product.usageFrequency),
      isAvailable: product.isAvailable ?? true,
      isRequiredProduct: product.isRequiredProduct ?? false,
      isBlocked: product.isBlocked ?? false,
      hasTimeRestriction: product.hasTimeRestriction ?? false,
      availableFrom: product.availableFrom ? new Date(product.availableFrom).toISOString().slice(0, 16) : '',
      availableTo: product.availableTo ? new Date(product.availableTo).toISOString().slice(0, 16) : '',
      imageUrl: product.imageUrl || ''
    });
  };

  const handleDelete = (productId: string) => {
    if (confirm('Tem certeza que deseja excluir este produto?')) {
      deleteProductMutation.mutate(productId);
    }
  };

  const handleCancel = () => {
    setShowCreateForm(false);
    setEditingProduct(null);
    resetProductForm();
  };

  if (isLoadingProducts) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Produtos</h2>
        <button 
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="btn btn-primary flex items-center gap-2"
        >
          <CubeIcon className="h-4 w-4" />
          {showCreateForm ? 'Cancelar' : 'Adicionar Produto'}
        </button>
      </div>

      {/* Formulário para criar/editar produto */}
      {(showCreateForm || editingProduct) && (
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingProduct ? 'Editar Produto' : 'Novo Produto'}
          </h3>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Informações básicas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nome do Produto *
                </label>
                <input
                  type="text"
                  required
                  value={productForm.name}
                  onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="Ex: Café Expresso"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Categoria *
                </label>
                <select
                  required
                  value={productForm.category}
                  onChange={(e) => setProductForm(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="Coffee">Café</option>
                  <option value="Food">Comida</option>
                  <option value="Drink">Bebida</option>
                  <option value="Dessert">Sobremesa</option>
                  <option value="Other">Outro</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Descrição
              </label>
              <textarea
                value={productForm.description}
                onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Descreva o produto..."
              />
            </div>

            {/* Preços */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preço Original (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  required
                  value={productForm.originalPrice}
                  onChange={(e) => setProductForm(prev => ({ ...prev, originalPrice: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Preço com Desconto (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={productForm.discountedPrice}
                  onChange={(e) => setProductForm(prev => ({ ...prev, discountedPrice: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.00"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  % de Desconto
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={productForm.discountPercentage}
                  onChange={(e) => setProductForm(prev => ({ ...prev, discountPercentage: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>

            {/* Configurações avançadas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Frequência de Uso
                </label>
                <select
                  value={productForm.usageFrequency}
                  onChange={(e) => setProductForm(prev => ({ ...prev, usageFrequency: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="unlimited">Ilimitado (Usuário pode usar quantas vezes quiser)</option>
                  <option value="daily">Diário (Pode usar uma vez ao dia)</option>
                  <option value="weekly">Semanal (Uma vez a cada 7 dias)</option>
                  <option value="biweekly">Quinzenal (Uma vez a cada 15 dias)</option>
                  <option value="monthly">Mensal (Uma vez a cada 30 dias)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Imagem do Produto
                </label>
                <ImageUpload
                  value={productForm.imageUrl}
                  onChange={(url) => setProductForm(prev => ({ ...prev, imageUrl: url }))}
                  onError={(error) => alert(error)}
                />
              </div>
            </div>

            {/* Checkboxes de configuração */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isAvailable"
                    checked={productForm.isAvailable}
                    onChange={(e) => setProductForm(prev => ({ ...prev, isAvailable: e.target.checked }))}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isAvailable" className="ml-2 block text-sm text-gray-900">
                    Produto Disponível
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isRequiredProduct"
                    checked={productForm.isRequiredProduct}
                    onChange={(e) => setProductForm(prev => ({ ...prev, isRequiredProduct: e.target.checked }))}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isRequiredProduct" className="ml-2 block text-sm text-gray-900">
                    Produto Obrigatório
                  </label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="isBlocked"
                    checked={productForm.isBlocked}
                    onChange={(e) => setProductForm(prev => ({ ...prev, isBlocked: e.target.checked }))}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label htmlFor="isBlocked" className="ml-2 block text-sm text-gray-900">
                    Produto Bloqueado
                  </label>
                </div>
              </div>
              
              <div className="space-y-4">
                {/* <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="hasTimeRestriction"
                    checked={productForm.hasTimeRestriction}
                    onChange={(e) => setProductForm(prev => ({ ...prev, hasTimeRestriction: e.target.checked }))}
                    className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                  />
                  <label htmlFor="hasTimeRestriction" className="ml-2 block text-sm text-gray-900">
                    Restrição de Horário
                  </label>
                </div> */}
                
                {productForm.hasTimeRestriction && (
                  <div className="grid grid-cols-2 gap-4 ml-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Disponível de
                      </label>
                      <input
                        type="datetime-local"
                        value={productForm.availableFrom}
                        onChange={(e) => setProductForm(prev => ({ ...prev, availableFrom: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Disponível até
                      </label>
                      <input
                        type="datetime-local"
                        value={productForm.availableTo}
                        onChange={(e) => setProductForm(prev => ({ ...prev, availableTo: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={createProductMutation.isPending || updateProductMutation.isPending}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
              >
                {createProductMutation.isPending || updateProductMutation.isPending 
                  ? 'Salvando...' 
                  : editingProduct ? 'Atualizar Produto' : 'Criar Produto'
                }
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de produtos */}
      {products && products.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Produto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Categoria
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Preços
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Configurações
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {products.map((product: any) => (
                <tr key={product.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {product.imageUrl && (
                        <img 
                          src={product.imageUrl} 
                          alt={product.name}
                          className="h-10 w-10 rounded-lg object-cover mr-3"
                        />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{product.name}</div>
                        <div className="text-sm text-gray-500">{product.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {product.category}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div>Original: R$ {product.originalPrice?.toFixed(2) || '0.00'}</div>
                      {product.discountedPrice && (
                        <div className="text-green-600">
                          Desconto: R$ {product.discountedPrice.toFixed(2)}
                        </div>
                      )}
                      {product.discountPercentage && (
                        <div className="text-orange-600 text-xs">
                          {product.discountPercentage}% OFF
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        product.isAvailable 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {product.isAvailable ? 'Disponível' : 'Indisponível'}
                      </span>
                      {product.isBlocked && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          Bloqueado
                        </span>
                      )}
                      {product.isRequiredProduct && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                          Obrigatório
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-xs text-gray-500 space-y-1">
                      <div>Freq: {getUsageFrequencyLabel(product.usageFrequency)}</div>
                      {product.hasTimeRestriction && (
                        <div>
                          <div>De: {product.availableFrom ? new Date(product.availableFrom).toLocaleString('pt-BR') : 'N/A'}</div>
                          <div>Até: {product.availableTo ? new Date(product.availableTo).toLocaleString('pt-BR') : 'N/A'}</div>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button 
                        onClick={() => handleEdit(product)}
                        className="text-emerald-600 hover:text-emerald-900"
                      >
                        Editar
                      </button>
                      <button 
                        onClick={() => handleDelete(product.id)}
                        disabled={deleteProductMutation.isPending}
                        className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        Excluir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <CubeIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum produto cadastrado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Comece adicionando produtos para sua cafeteria.
          </p>
          <div className="mt-6">
            <button 
              onClick={() => setShowCreateForm(true)}
              className="btn btn-primary"
            >
              Adicionar Primeiro Produto
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// Componente para gerenciar usuários vinculados ao lugar
const PlaceUsersSection: React.FC<{ placeId: string }> = ({ placeId }) => {
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [linkForm, setLinkForm] = useState({
    businessUserId: '',
    role: 'Owner'
  });

  // Buscar usuários vinculados ao lugar
  const { data: placeUsers, isLoading: isLoadingUsers, refetch: refetchUsers } = useQuery({
    queryKey: ['place-users', placeId],
    queryFn: async () => {
      const response = await api.get(`/admin/places/${placeId}/users`);
      return response.data;
    },
    enabled: !!placeId
  });

  // Buscar todos os business users disponíveis para vincular
  const { data: availableUsers, isLoading: isLoadingAvailableUsers } = useQuery({
    queryKey: ['available-business-users'],
    queryFn: async () => {
      const response = await api.get('/admin/business-users', {
        params: {
          page: 1,
          pageSize: 50
        }
      });
      return response.data.businessUsers;
    }
  });

  // Mutação para vincular usuário
  const linkUserMutation = useMutation({
    mutationFn: async (data: { businessUserId: string; role: string }) => {
      const response = await api.post(`/admin/places/${placeId}/users`, data);
      return response.data;
    },
    onSuccess: () => {
      refetchUsers();
      setShowLinkForm(false);
      setLinkForm({ businessUserId: '', role: 'Owner' });
    },
    onError: (error: any) => {
      console.error('Erro ao vincular usuário:', error);
      alert('Erro ao vincular usuário');
    }
  });

  // Mutação para desvincular usuário
  const unlinkUserMutation = useMutation({
    mutationFn: async (businessUserId: string) => {
      const response = await api.delete(`/admin/places/${placeId}/users/${businessUserId}`);
      return response.data;
    },
    onSuccess: () => {
      refetchUsers();
    },
    onError: (error: any) => {
      console.error('Erro ao desvincular usuário:', error);
      alert('Erro ao desvincular usuário');
    }
  });

  const handleLinkUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!linkForm.businessUserId) {
      alert('Selecione um usuário');
      return;
    }
    linkUserMutation.mutate(linkForm);
  };

  const handleUnlinkUser = (businessUserId: string) => {
    if (confirm('Tem certeza que deseja desvincular este usuário?')) {
      unlinkUserMutation.mutate(businessUserId);
    }
  };

    // Filtrar usuários que não estão vinculados
  const availableUsersToLink = availableUsers?.filter((user: any) =>
    !placeUsers?.users?.some((placeUser: any) => placeUser.id === user.id)
  ) || [];

  if (isLoadingUsers || isLoadingAvailableUsers) {
    return (
      <div className="p-6">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Usuários Vinculados</h2>
        <button 
          onClick={() => setShowLinkForm(!showLinkForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <UserIcon className="h-4 w-4" />
          {showLinkForm ? 'Cancelar' : 'Vincular Usuário'}
        </button>
      </div>

      {/* Formulário para vincular usuário */}
      {showLinkForm && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Vincular Novo Usuário</h3>
          <form onSubmit={handleLinkUser} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Usuário *
                </label>
                <select
                  required
                  value={linkForm.businessUserId}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, businessUserId: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="">Selecione um usuário</option>
                  {availableUsersToLink.map((user : any) => (
                    <option key={user.id} value={user.id}>
                      {user.fullName} ({user.email})
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Função na Cafeteria *
                </label>
                <select
                  required
                  value={linkForm.role}
                  onChange={(e) => setLinkForm(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                >
                  <option value="Owner">Proprietário</option>
                  <option value="Admin">Administrador</option>
                  <option value="Manager">Gerente</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setShowLinkForm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={linkUserMutation.isPending}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {linkUserMutation.isPending ? 'Vinculando...' : 'Vincular Usuário'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de usuários vinculados */}
      {placeUsers?.users && placeUsers.users.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Função
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Vinculado em
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {placeUsers.users.map((user : any) => (
                <tr key={user.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{user.fullName}</div>
                      <div className="text-sm text-gray-500">{user.email}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                      {user.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(user.linkedAt).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleUnlinkUser(user.id)}
                      disabled={unlinkUserMutation.isPending}
                      className="text-red-600 hover:text-red-800 disabled:opacity-50"
                      title="Desvincular usuário"
                    >
                      Desvincular
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário vinculado</h3>
          <p className="mt-1 text-sm text-gray-500">
            Vincule usuários para gerenciar esta cafeteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default PlaceEditPage;
